import AccordionDetails from "@mui/material/AccordionDetails";
import Grid from "@mui/material/Grid2";
import Button from "@mui/material/Button";
import React, {useEffect, useState} from "react";
import ProjectDetailValidatedTextField from "./ProjectDetailValidatedTextField";
import {alphaNumericValidator, checkAllValid, numberValidator} from "../../Helpers/ValidationHelpers";
import ProjectDetailValidatedDropdown from "./ProjectDetailValidatedDropdown";
import Box from "@mui/material/Box";
import {produce} from "immer";
import ProjectDetailSaveAsTemplatePrompt from "./ProjectDetailSaveAsTemplatePrompt";
import ProjectDetailTemplateNameField from "./ProjectDetailTemplateNameField";
import ProjectDetailUpdateTemplatePrompts from "./ProjectDetailUpdateTemplatePrompts";
import ProjectDetailCheckbox from "./ProjectDetailCheckbox";

function ProjectDetailRowItemAccordionDetails({
                                                quoteDetailId,
                                                handleDetailChange,
                                                productItem,
                                                isSubmitting,
                                                setExpanded,
                                                savedQuoteDetail,
                                                setSavedQuoteDetail,
                                                initialTemplateQuoteDetail, 
                                                allQuoteDetailsSaved,
                                                setAllQuoteDetailsSaved, 
                                                formValidQuoteDetail,
                                                draftQuoteDetail,
                                                setDraftQuoteDetail, 
                                                quoteTemplates,
                                                activeForm, 
                                                isUpdate
                                            }) 
{
    const [updateTemplateChecked, setUpdateTemplateChecked] = useState(false);
    const [showTemplateNewField, setShowTemplateNewField] = useState(false);
    const [showTemplateUpdateField, setShowTemplateUpdateField] = useState(false);
    const [templateNameValue, setTemplateNameValue] = useState('');
    const [error, setError] = useState(false);

    const equal = require('fast-deep-equal');
    
    useEffect(() => {
        // Set unsaved if quote detail is updated after a save
        if (!equal(draftQuoteDetail, savedQuoteDetail)) {
            setAllQuoteDetailsSaved(prevState => ({
                ...prevState,
                [quoteDetailId]: false
            }));
        }

        // Show an option to update if template is edited
        if (initialTemplateQuoteDetail && !equal(draftQuoteDetail, initialTemplateQuoteDetail)) {
            setShowTemplateUpdateField(true);
        } else {
            setShowTemplateUpdateField(false);
        }
    }, [draftQuoteDetail])

    useEffect(() => {
        setDraftQuoteDetail(produce(draftQuoteDetail, draft => {
            draft.templateName = null;
            draft.templateId = null;
        }));
        setShowTemplateNewField(false);
    }, [draftQuoteDetail.productGroupId, draftQuoteDetail.productItemId, draftQuoteDetail.templateDetails])
    
    const handleTemplateNameChange = (e) => {
        const { value: newValue } = e.target;
        const errorMessage = alphaNumericValidator(newValue)
            || (quoteTemplates.some(x => x.name === newValue) 
                ? `Template '${newValue}' already exists.`
                : false) 
            || (activeForm?.quoteDetails
                ?.filter(detail => detail.tempId !== quoteDetailId || detail.id !== quoteDetailId )
                ?.some(x => x.templateName === newValue) 
                ? `Template '${newValue}' is already being saved in another quote item.` 
                : false);
        setError(errorMessage);
        formValidQuoteDetail.current.templateName = !errorMessage;
        setTemplateNameValue(newValue);
        setDraftQuoteDetail(produce(draftQuoteDetail, draft => {
            draft.templateName = newValue;
        }));
    }
    
    const handleSaveItemDetail = () => {
        setExpanded(false);
        let quoteDetailToSet;
        // Update template
        if (!!draftQuoteDetail.templateName && !!draftQuoteDetail.templateId) {
            quoteDetailToSet =  {
                ...draftQuoteDetail,
                templateId: draftQuoteDetail?.templateDetails?.templateId,
                templateName: draftQuoteDetail?.templateDetails?.templateName,
            };
        // Create Template
        } else if (!!draftQuoteDetail.templateName) {
            quoteDetailToSet = {
                ...draftQuoteDetail,
                templateName: templateNameValue,
                templateId: null
            };
        // No template creation/update
        }  else {
            quoteDetailToSet = {
                ...draftQuoteDetail,
                templateName: null,
                templateId: null
            };
        }

        handleDetailChange(quoteDetailId, quoteDetailToSet)
        setAllQuoteDetailsSaved(prevState => ({
            ...prevState,
            [quoteDetailId]: true
        }));
        setSavedQuoteDetail(quoteDetailToSet);
    }
    
    return (
        <AccordionDetails sx={{ pt: 2 }}>
            <Grid container 
                  spacing={2}
                  columnSpacing={2}
                  alignItems="center" 
                  justifyContent={{xs: 'left', md: 'flex-start'}}
            >
                {productItem.fields.map((field, index) => {
                        if (field.type === 1 || field.type === "Text") {
                            return (
                                <Grid size={{ xs: 12 }} key={field.id} >
                                    <ProjectDetailValidatedTextField
                                        draftQuoteDetail={draftQuoteDetail}
                                        setDraftQuoteDetail={setDraftQuoteDetail}
                                        id={index} 
                                        disabled={isSubmitting}
                                        field={field}
                                        onChange={isValid => formValidQuoteDetail.current[field.id || field.fieldId] = isValid}
                                        validator={alphaNumericValidator} 
                                        productItem={productItem}
                                    />
                                </Grid>
                            )
                        } else if (field.type === "Number" || field.type === 2) {
                            return (
                                <Grid size={{ xs: 12 }} key={field.id} >
                                    <ProjectDetailValidatedTextField
                                        draftQuoteDetail={draftQuoteDetail}
                                        setDraftQuoteDetail={setDraftQuoteDetail}
                                        id={index}
                                        disabled={isSubmitting}
                                        field={field}
                                        onChange={isValid => formValidQuoteDetail.current[field.id || field.fieldId] = isValid}
                                        validator={numberValidator}
                                        productItem={productItem}
                                    />
                                </Grid>
                            )
                        } else if (field.type === 3 || field.type === "Enumeration") {
                            return (
                                <Grid size={{ xs: 12 }} key={field.id}>
                                    <ProjectDetailValidatedDropdown
                                        draftQuoteDetail={draftQuoteDetail}
                                        setDraftQuoteDetail={setDraftQuoteDetail}
                                        id={index} 
                                        disabled={isSubmitting}
                                        field={field}
                                        onChange={isValid => formValidQuoteDetail.current[field.id || field.fieldId] = isValid}
                                        productItem={productItem}
                                    />
                                </Grid>
                            )
                        } else if (field.type === 4 || field.type === "Boolean") {
                            return (
                                <Grid size={{ xs: 12 }} key={field.id}>
                                    <ProjectDetailCheckbox
                                        field={field} 
                                        disabled={isSubmitting}
                                        setDraftQuoteDetail={setDraftQuoteDetail} 
                                        draftQuoteDetail={draftQuoteDetail}
                                        productItemId={productItem.id} />
                                </Grid>
                            )
                        }
                    }
                )}
                {
                    !isUpdate &&
                    <Grid size={{ xs: 12 }}>
                        <ProjectDetailUpdateTemplatePrompts
                            draftQuoteDetail={draftQuoteDetail}
                            showTemplateNewField={showTemplateNewField}
                            setDraftQuoteDetail={setDraftQuoteDetail}
                            formValidQuoteDetail={formValidQuoteDetail}
                            setShowTemplateNewField={setShowTemplateNewField}
                            showTemplateUpdateField={showTemplateUpdateField}
                            updateTemplateChecked={updateTemplateChecked}
                            setUpdateTemplateChecked={setUpdateTemplateChecked}
                        />
                        {
                            productItem.fields.length > 0 &&
                            <Box display="flex" alignItems="center">
                                <ProjectDetailSaveAsTemplatePrompt
                                    showTemplateNewField={showTemplateNewField}
                                    setShowTemplateNewField={setShowTemplateNewField}
                                    draftQuoteDetail={draftQuoteDetail}
                                    setDraftQuoteDetail={setDraftQuoteDetail}
                                    formValidQuoteDetail={formValidQuoteDetail}
                                />
                            </Box>
                        }
                        <ProjectDetailTemplateNameField
                            draftQuoteDetail={draftQuoteDetail}
                            showTemplateNewField={showTemplateNewField}
                            handleTemplateNameChange={handleTemplateNameChange}
                            error={error}
                        />
                    </Grid>
                }
                <Grid size={{ xs: 12 }}>
                    <Box sx={{ mb: 2 }}>
                        <Button
                            variant="contained"
                            onClick={handleSaveItemDetail}
                            disabled={isSubmitting || !checkAllValid(formValidQuoteDetail.current) || allQuoteDetailsSaved[quoteDetailId] }
                            sx={{ mt: 1, mr: 1 }}
                        >
                            Save
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </AccordionDetails>
    );
}

export default ProjectDetailRowItemAccordionDetails;